import React, {
  forwardRef,
  useCallback,
  useContext,
  type Ref,
} from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Activity, Message } from 'botframework-directlinejs';

import { DEFAULT_GROUP_TIME } from './constants';
import { makeActivityGroups } from './make-activity-groups';
import { DefaultRenderActivity, IConversationThreadPropsBase } from '../../types';
import { DirectLineContext } from '../direct-line-provider/direct-line-context';
import { ActivityGroup } from '../../ActivityGroup';
import { AIMessage } from '../message/ai-message';
import { HumanMessage } from '../message/human-message';
import { getActivityKey } from './get-activity-key';
import { getIsFromUser, ActivityGroupContainerClassName } from '../../utils';

interface IConversationThreadPropsInternal extends IConversationThreadPropsBase {
  activities: Activity[];
  conversationId?: string;
}

const useConversationThreadStyles = makeStyles({
  root: {
  },
  top: {
    paddingBottom: '40px',
  },
  activityGroupContainer: {
    paddingBottom: tokens.spacingHorizontalXXS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    '& .activity-group': {
      marginTop: tokens.spacingVerticalM,
    },
    '& .activity-group:first-child': {
      marginTop: 0,
    },
  },
  bottom: {},
});

const defaultRenderActivity: DefaultRenderActivity = (activity, params) => {
  const {
    action,
    key,
    onFeedbackButtonClick,
    onReferenceClick,
    username,
    footer,
    isShowFeaturesInHint,
    isMessagePopoverEnabled,
    isFinalActivity,
    isLastActivityWithEventCalls,
    getAIMessageMarkdownOverrides,
  } = params;
  const isAIMessage = !getIsFromUser(activity, username);

  if (isAIMessage) {
    return (
      <AIMessage
        key={key}
        message={activity as Message}
        onFeedbackButtonClick={onFeedbackButtonClick}
        action={action}
        footer={footer}
        onReferenceClick={onReferenceClick}
        isShowFeaturesInHint={isShowFeaturesInHint}
        isMessagePopoverEnabled={isMessagePopoverEnabled}
        isFinalActivity={isFinalActivity}
        isLastActivityWithEventCalls={isLastActivityWithEventCalls}
        getAIMessageMarkdownOverrides={getAIMessageMarkdownOverrides}
      />
    );
  }

  return (
    <HumanMessage
      key={activity.id}
      message={activity as Message}
    />
  );
};

const ConversationThreadInternalWithForwardRef = (props: IConversationThreadPropsInternal, ref: Ref<HTMLDivElement>) => {
  const {
    activities,
    conversationId = '',
    groupTime = DEFAULT_GROUP_TIME,
    isCopilotDegraded = false,
    isTroubleshootingEnabled = false,
    isReportingInsightsEnabled = false,
    isShowFeaturesInHint = false,
    isMessagePopoverEnabled = false,
    onFeedbackButtonClick,
    onReferenceClick,
    onSuggestionClick,
    renderAction,
    renderActivity,
    renderActivityFooter,
    renderBottomComponent,
    renderChatSuggestionList,
    renderTopComponent,
    getAIMessageMarkdownOverrides,
  } = props;
  const styles = useConversationThreadStyles();

  const {
    directLine, username,
  } = useContext(DirectLineContext)!;

  const activityGroups = makeActivityGroups({
    activities,
    groupTime,
    isTroubleshootingEnabled,
    isReportingInsightsEnabled,
  });

  const finalRenderActivity = useCallback((activity: Message) => {
    const lastActivity = activities[activities.length - 1];
    const isFinal = activities.length > 0 && lastActivity?.id === activity.id;
    const lastActivityWithEventCalls = [...activities].reverse().find((a: any) => a.value?.EventCalls?.length > 0);
    const isLastActivityWithEventCalls = lastActivityWithEventCalls?.id === activity.id;
    const params = {
      onFeedbackButtonClick,
      username,
      isShowFeaturesInHint,
      currentActivities: activities,
      directLine,
      onReferenceClick,
      action: renderAction ? renderAction(activity) : undefined,
      footer: renderActivityFooter ? renderActivityFooter(activity) : undefined,
      key: getActivityKey(activity),
      isMessagePopoverEnabled,
      isFinalActivity: isFinal,
      isLastActivityWithEventCalls,
      getAIMessageMarkdownOverrides,
    };

    if (renderActivity) {
      return renderActivity(activity, params, defaultRenderActivity);
    }
    return defaultRenderActivity(activity, params);
  }, [activities, directLine, onFeedbackButtonClick, onReferenceClick, renderAction, renderActivityFooter, renderActivity, username]);

  return (
    <div data-conversation-id={conversationId} className={mergeClasses(styles.root, 'conversation-thread')} role="log">
      <div className={mergeClasses(isCopilotDegraded ? styles.top : '', 'conversation-thread-top')}>
        {renderTopComponent && renderTopComponent(activities, onSuggestionClick)}
      </div>
      <div className={mergeClasses(styles.activityGroupContainer, ActivityGroupContainerClassName)} ref={ref}>
        {activityGroups.map(actGroup => (
          <ActivityGroup
            key={actGroup.key}
            activities={actGroup.activities}
            renderActivity={finalRenderActivity}
          />
        ))}
      </div>
      <div className={mergeClasses(styles.bottom, 'conversation-thread-bottom')}>
        {renderBottomComponent && renderBottomComponent(activities)}
      </div>
      {!isCopilotDegraded && renderChatSuggestionList && renderChatSuggestionList()}
    </div>
  );
};

export const ConversationThreadInternal = forwardRef(ConversationThreadInternalWithForwardRef);
